import type { BuildingBounds } from "building-types/building/buildingBounds";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { GarageSettings } from "building-types/settings/garageSettings";
import type { GeneratedSite, Site } from "building-types/site/site";
import type { Table } from "common/data/table";
import { exteriorMaterialOverrideNew } from "doc/utils/materialTools";
import { generatedSiteNew } from "doc/utils/siteTools";
import { DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import GeomTagGroup from "geom/geomTagGroup";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2ByTwoPoints, polyLine2Extend, polyLine2Length, polyLine2Move, polyLine2SetCW } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import { polyLine3Copy, polyLine3Move, polyLine3New } from "geom/polyLine3";
import { rect2ByTwoPoints } from "geom/rect2";
import type { SiteZGeom } from "./siteUtils";
import { getSiteDoorData, getSiteZ, SiteDoorType } from "./siteUtils";

interface MakeDynamicSiteOptions {
  readonly site: Site;
  readonly doorPlacements: Table<GeneratedDoorPlacement>;
  readonly buildingBounds: BuildingBounds;
  readonly planarGraphs: Table<PolygonPlanarGraph>;
  readonly garageSettings: GarageSettings;
  readonly levels: Table<Level>;
}

export function makeWhiteBoxSite(options: MakeDynamicSiteOptions): GeneratedSite | undefined {
  const { site, doorPlacements, buildingBounds, planarGraphs, garageSettings, levels } = options;
  const {
    frontSetback = 0,
    sideSetback = 0,
    backSetback = 0,
    sidewalkWidth = 0,
    grassCurbWidth = 0,
    curbHeight = 0,
    sidewalkRampLength = 0,
    // roadWidth,
    roadVerticalOffset = 0,
    homeBackVerticalOffset = 0,
    homeFrontVerticalOffset = 0,
    sidewalk,
    curb,
    frontWalk,
    // frontWalkWidth,
    frontWalkConnectedToDriveway,
    frontWalkConnectedSetback = 0,
    driveway,
  } = site;

  // approximate a dynamic site
  const ret = generatedSiteNew(site);

  if (!buildingBounds.aabb) {
    return undefined;
  }

  const { minY: houseFront, maxY: houseBack, minX: houseLeft, maxX: houseRight } = buildingBounds.aabb;
  const houseLowPoint = buildingBounds.buildingBottomElevation;

  const garageDoorData = getSiteDoorData(levels, planarGraphs, doorPlacements, SiteDoorType.GARAGE);
  if (!garageDoorData) {
    return undefined;
  }

  const { doorElevation: garageElevation, drivewayInfo } = garageDoorData;

  const entryDoorData = getSiteDoorData(levels, planarGraphs, doorPlacements, SiteDoorType.ENTRY);
  if (!entryDoorData) {
    return undefined;
  }

  const { doorElevation: frontDoorElevation, drivewayInfo: frontDoorInfo } = entryDoorData;

  const houseDepth = buildingBounds.aabb.maxY - buildingBounds.aabb.minY;
  const siteBack = houseBack + backSetback + 1;

  // set the front boundary to take the front of the house and extend it by the front setback
  const frontBoundary = buildingBounds.aabb.minY - frontSetback;

  // define the rear of the home and adjust the low point to clear the foundation.
  const siteAdjustZ = 12;
  const siteBackVerticalOffset = houseLowPoint - siteAdjustZ + homeBackVerticalOffset;

  const siteBoundaryLeft = houseLeft - sideSetback;
  let siteBoundaryRight = houseRight + sideSetback;

  // make the Topography
  const leftTopoRail = polyLine3New([
    siteBoundaryLeft,
    frontBoundary,
    garageElevation + roadVerticalOffset,

    siteBoundaryLeft,
    frontBoundary + sidewalkWidth + grassCurbWidth,
    garageElevation + roadVerticalOffset,

    siteBoundaryLeft,
    houseFront,
    garageElevation + homeFrontVerticalOffset,

    siteBoundaryLeft,
    frontBoundary + houseDepth + frontSetback,
    siteBackVerticalOffset - 6,

    siteBoundaryLeft,
    siteBack,
    siteBackVerticalOffset - 6,
  ]);

  const topoProfile = polyLine2New([0, -2, siteBoundaryRight - siteBoundaryLeft, -2]);

  // initialize some variables that will be set based on analysis
  let driveStartX = -1;
  let driveEndX = -1;

  // define sides of site, assumes home is centered on the site boundary and the site boundary is a rectangle
  // need ability to set the origin of the home relative to site and alignment

  if (driveway) {
    for (const info of drivewayInfo) {
      const { normalX, normalY, startX, startY, endX, endY } = info;
      const doorLine = polyLine2ByTwoPoints(startX, startY, endX, endY);
      polyLine2Extend(doorLine, garageSettings.drivewayDoorOffset, garageSettings.drivewayDoorOffset);

      const drivewayLeftRail = polyLine3New([
        doorLine[0],
        frontBoundary,
        garageElevation + roadVerticalOffset,
        doorLine[0],
        frontBoundary + sidewalkWidth + grassCurbWidth,
        garageElevation + roadVerticalOffset,
        doorLine[0],
        houseFront,
        garageElevation + homeFrontVerticalOffset,
        doorLine[0],
        doorLine[1],
        garageElevation,
      ]);

      if (driveStartX === -1) {
        driveStartX = startX;
      } else if (startX < driveStartX) {
        driveStartX = startX;
      }

      if (driveEndX === -1) {
        driveEndX = endX;
      } else if (driveEndX < endX) {
        driveEndX = endX;
      }

      if (Math.abs(normalY) > 1.0 - DIST_EPSILON) {
        const drivewayProfile = rect2ByTwoPoints(0, -8, doorLine[2] - doorLine[0], 0);
        polyLine2SetCW(drivewayProfile);
        ret.drivewayGeom.push({
          group: new GeomTagGroup<ExteriorMaterialOverride>().add(
            extrudeFaceAlongPolyLine3([drivewayProfile], drivewayLeftRail, 0, 0, 1),
            exteriorMaterialOverrideNew("driveway0"),
          ),
          pickHandle: undefined,
        });
      } else {
        siteBoundaryRight += 360;
        // this is the side garage condition.
        // const sideStart = doorLine[1] > doorLine[3] ? doorLine.slice(0, 2) : doorLine.slice(2, 4);
        // const sideEnd = doorLine[1] < doorLine[3] ? doorLine.slice(0, 2) : doorLine.slice(2, 4);
        // const drivewayNormalY = -1; // driveway normal is 0, -1 right now

        // const siteEnd = frontBoundary;
        // const drivewayLength = sideStart[1] - houseFront + frontSetback;

        const houseEdge = normalX < 0 ? buildingBounds.aabb.minX : buildingBounds.aabb.maxX;
        const borderEdge = houseEdge + normalX * garageSettings.sideDrivewayMargin;

        const drivewayWidth = polyLine2Length(doorLine);
        const sideOut = borderEdge + normalX * drivewayWidth;

        normalX < 0 ? (driveStartX = sideOut) : (driveStartX = borderEdge);

        normalX < 0 ? (driveEndX = borderEdge) : (driveEndX = sideOut);

        const sideDriveOffset = doorLine[0] + garageSettings.sideDrivewayMargin;

        const drivewayLeftRail = polyLine3New([
          sideDriveOffset,
          frontBoundary,
          garageElevation + roadVerticalOffset,

          sideDriveOffset,
          frontBoundary + sidewalkWidth + grassCurbWidth,
          garageElevation + roadVerticalOffset,

          sideDriveOffset,
          houseFront,
          garageElevation + homeFrontVerticalOffset,

          sideDriveOffset,
          doorLine[1],
          garageElevation,

          doorLine[0],
          doorLine[1],
          garageElevation,
        ]);

        const drivewayProfile = rect2ByTwoPoints(0, -6, drivewayWidth, 2);
        polyLine2SetCW(drivewayProfile);
        ret.drivewayGeom.push({
          group: new GeomTagGroup<ExteriorMaterialOverride>().add(
            extrudeFaceAlongPolyLine3([drivewayProfile], drivewayLeftRail, 0, 0, 1),
            exteriorMaterialOverrideNew("driveway0"),
          ),
          pickHandle: undefined,
        });
      }
    }
  }

  // more complex topography controls are needed
  const topoSrfGeom = extrudeFaceAlongPolyLine3([topoProfile], leftTopoRail, 0, 0, 1);
  const topoSrf: SiteZGeom = {
    geometry: topoSrfGeom,
  };
  ret.siteWhiteBoxGeom.add(topoSrfGeom, exteriorMaterialOverrideNew("driveway0"));

  if (frontWalk) {
    for (const info of frontDoorInfo) {
      const { normalX, normalY, startX, startY, endX, endY } = info;

      if (Math.abs(normalX) > 1.0 - DIST_EPSILON) {
        // side facing
        continue;
      }

      const doorLine = polyLine2ByTwoPoints(startX, startY, endX, endY);

      if (!frontWalkConnectedToDriveway || !driveway) {
        const frontWalkLeftRail = polyLine3New([
          doorLine[0],
          doorLine[1],
          frontDoorElevation - 6,

          doorLine[0],
          doorLine[1] - 36,
          frontDoorElevation - 6,

          doorLine[0],
          houseFront,
          garageElevation + homeFrontVerticalOffset,

          doorLine[0],
          frontBoundary + sidewalkWidth + grassCurbWidth,
          garageElevation + roadVerticalOffset,
        ]);

        // ret.setbackDatum.push(frontWalkLeftRail);

        if (Math.abs(normalY) > 1.0 - DIST_EPSILON) {
          const frontWalkProfile = rect2ByTwoPoints(0, -24, -(doorLine[2] - doorLine[0]), 0);
          polyLine2SetCW(frontWalkProfile);
          ret.drivewayGeom.push({
            group: new GeomTagGroup<ExteriorMaterialOverride>().add(
              extrudeFaceAlongPolyLine3([frontWalkProfile], frontWalkLeftRail, 0, 0, 1),
              exteriorMaterialOverrideNew("driveway0"),
            ),
            pickHandle: undefined,
          });
        }
      } else {
        const frontWalkLeftRail = polyLine3New([
          doorLine[0],
          doorLine[1],
          frontDoorElevation - 6,

          doorLine[0],
          houseFront,
          getSiteZ(topoSrf, doorLine[0], houseFront),

          doorLine[0],
          houseFront - frontWalkConnectedSetback,
          getSiteZ(topoSrf, doorLine[0], houseFront - frontWalkConnectedSetback),

          driveStartX > doorLine[0] ? driveStartX : driveEndX,
          houseFront - frontWalkConnectedSetback,
          getSiteZ(topoSrf, driveStartX, houseFront - frontWalkConnectedSetback),
        ]);

        if (Math.abs(normalY) > 1.0 - DIST_EPSILON) {
          const frontWalkProfile = rect2ByTwoPoints(0, -24, -(doorLine[2] - doorLine[0]), 2);
          polyLine2SetCW(frontWalkProfile);
          ret.drivewayGeom.push({
            group: new GeomTagGroup<ExteriorMaterialOverride>().add(
              extrudeFaceAlongPolyLine3([frontWalkProfile], frontWalkLeftRail, 0, 0, 1),
              exteriorMaterialOverrideNew("driveway0"),
            ),
            pickHandle: undefined,
          });
        }
      }
    }
  }

  const leftSetbackRail = polyLine3Copy(leftTopoRail);
  const rightSetbackRail = polyLine3Copy(leftTopoRail);
  // polyLine3Move(leftSetbackRail, -(siteContextLeft - houseLeft + sideSetback), 0, 0);
  polyLine3Move(rightSetbackRail, siteBoundaryRight - siteBoundaryLeft, 0, 0);

  const printLeftSiteEdges = leftSetbackRail;
  const printRightSiteEdges = rightSetbackRail;

  // slice up the rails to get the site outline
  ret.setbackDatum.push(printLeftSiteEdges);
  ret.setbackDatum.push(printRightSiteEdges);

  const rearSetback = polyLine3New([
    siteBoundaryLeft,
    siteBack,
    siteBackVerticalOffset - 6,

    siteBoundaryRight,
    siteBack,
    siteBackVerticalOffset - 6,
  ]);

  ret.setbackDatum.push(rearSetback);

  const curbBaseProfile = rect2ByTwoPoints(0, 0, 24, 1);
  polyLine2SetCW(curbBaseProfile);
  polyLine2Move(curbBaseProfile, 0, 0);

  const houseFrontRail = polyLine3New([
    siteBoundaryLeft,
    frontBoundary,
    garageElevation + roadVerticalOffset,

    siteBoundaryRight,
    frontBoundary,
    garageElevation + roadVerticalOffset,
  ]);

  ret.setbackDatum.push(houseFrontRail);

  if (sidewalk || curb) {
    const sidewalkProfile = rect2ByTwoPoints(0, 0, -sidewalkWidth, curbHeight);
    polyLine2SetCW(sidewalkProfile);
    polyLine2Move(sidewalkProfile, -grassCurbWidth, 0);

    const grassCurbProfile = rect2ByTwoPoints(0, 0, -grassCurbWidth, curbHeight - 1);
    polyLine2SetCW(grassCurbProfile);

    const sidewalkRail1 = polyLine3New([
      siteBoundaryLeft,
      frontBoundary,
      garageElevation + roadVerticalOffset,
      driveStartX - sidewalkRampLength,
      frontBoundary,
      garageElevation + roadVerticalOffset,
      driveStartX,
      frontBoundary,
      garageElevation - curbHeight + roadVerticalOffset,
    ]);

    const sidewalkRail2 = polyLine3New([
      driveEndX,
      frontBoundary,
      garageElevation - curbHeight + roadVerticalOffset,
      driveEndX + sidewalkRampLength,
      frontBoundary,
      garageElevation + roadVerticalOffset,
      siteBoundaryRight,
      frontBoundary,
      garageElevation + roadVerticalOffset,
    ]);

    if (sidewalk) {
      const sidewalk1 = extrudeFaceAlongPolyLine3([sidewalkProfile], sidewalkRail1, 0, 0, 1);
      const sidewalk2 = extrudeFaceAlongPolyLine3([sidewalkProfile], sidewalkRail2, 0, 0, 1);

      ret.sidewalkGeom.push({
        group: new GeomTagGroup<ExteriorMaterialOverride>().add(sidewalk1, exteriorMaterialOverrideNew("driveway0")),
        pickHandle: { type: "SITE", id: ret.id },
      });
      ret.sidewalkGeom.push({
        group: new GeomTagGroup<ExteriorMaterialOverride>().add(sidewalk2, exteriorMaterialOverrideNew("driveway0")),
        pickHandle: { type: "SITE", id: ret.id },
      });
    }
  }

  return {
    ...ret,
  };
}
